import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { SunDim, FilePdf, FileImage, CircleNotch, SealCheck } from "@phosphor-icons/react";
import { API } from "../../lib/api";
import { MaterialIcon } from "../../lib/materialIcons";
import { Button } from "../ui/button";

const fmt = (n) => (Number.isInteger(n) ? n : Number(n).toFixed(2).replace(/\.00$/, ""));
export const slipUrl = (token) => `${window.location.origin}/d/${token}`;
const dmy = (d) => (d ? d.split("-").reverse().join("/") : "—");
const time12 = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${String(h % 12 || 12).padStart(2, "0")}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};

const usePhotoUrls = (slip) => {
  const [urls, setUrls] = useState({});
  useEffect(() => {
    let alive = true;
    const made = [];
    (slip.photos || []).forEach((p) => {
      axios.get(`${API}/public/dispatch/${slip.token}/photos/${p.id}`, { responseType: "blob" }).then((r) => {
        const u = URL.createObjectURL(r.data); made.push(u);
        if (alive) setUrls((s) => ({ ...s, [p.id]: u }));
      }).catch(() => {});
    });
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [slip.token, slip.photos]);
  return urls;
};

const Field = ({ label, value, mono, testid }) => (
  <div>
    <div className="text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">{label}</div>
    <div className={`text-[13px] font-semibold text-slate-900 ${mono ? "font-mono" : ""}`} data-testid={testid}>{value || "—"}</div>
  </div>
);

export function DispatchSlip({ slip }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState("");
  const photos = usePhotoUrls(slip);
  const totalQty = (slip.items || []).reduce((a, i) => a + Number(i.qty || 0), 0);

  const box = useRef(null);
  const [scale, setScale] = useState(1);
  const [h, setH] = useState(0);
  const [capturing, setCapturing] = useState(false);
  useEffect(() => {
    const fit = () => {
      if (box.current) setScale(Math.min(1, box.current.clientWidth / 794));
      if (ref.current) setH(ref.current.offsetHeight);
    };
    fit();
    const ro = new ResizeObserver(fit);
    if (box.current) ro.observe(box.current);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const s = capturing ? 1 : scale;

  const capture = async () => {
    setCapturing(true);
    await new Promise((r) => setTimeout(r, 120));
    try { return await html2canvas(ref.current, { scale: 2, useCORS: true, backgroundColor: "#ffffff" }); }
    finally { setCapturing(false); }
  };
  const download = async (kind) => {
    setBusy(kind);
    try {
      const canvas = await capture();
      const name = `${slip.dispatch_no || "dispatch"}-${(slip.customer_name || "").replace(/\s+/g, "_")}`;
      if (kind === "png") {
        const a = document.createElement("a");
        a.href = canvas.toDataURL("image/png"); a.download = `${name}.png`; a.click();
      } else {
        const pdf = new jsPDF({ unit: "mm", format: "a4" });
        const w = 210, h = 297, imgH = (canvas.height * w) / canvas.width;
        const img = canvas.toDataURL("image/jpeg", 0.92);
        for (let y = 0; y < imgH; y += h) {
          if (y > 0) pdf.addPage();
          pdf.addImage(img, "JPEG", 0, -y, w, imgH);
        }
        pdf.save(`${name}.pdf`);
      }
    } catch { toast.error("Download failed. Please try again."); }
    finally { setBusy(""); }
  };

  return (
    <div className="space-y-4" data-testid="dispatch-slip">
      <div className="flex flex-wrap gap-2 justify-end print:hidden">
        <Button data-testid="slip-download-pdf-btn" disabled={!!busy} onClick={() => download("pdf")} className="h-10 rounded-full bg-slate-900 hover:bg-slate-800 text-white">
          {busy === "pdf" ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <FilePdf size={16} className="mr-1.5" />} Download PDF
        </Button>
        <Button data-testid="slip-download-png-btn" variant="outline" disabled={!!busy} onClick={() => download("png")} className="h-10 rounded-full">
          {busy === "png" ? <CircleNotch size={16} className="mr-1.5 animate-spin" /> : <FileImage size={16} className="mr-1.5" />} Download Image
        </Button>
      </div>

      <div ref={box} className="w-full max-w-[794px] mx-auto overflow-hidden rounded-xl ring-1 ring-slate-200 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.25)]"
        style={{ height: h ? h * s : undefined }}>
        <div style={{ transform: `scale(${s})`, transformOrigin: "top left", width: 794 }}>
        <div ref={ref} className="bg-white text-slate-900 w-[794px] min-h-[600px]" data-testid="slip-sheet">
          <div className="bg-slate-950 text-white px-8 py-6 flex items-start justify-between">
            <div className="flex items-center gap-3">
              <span className="w-12 h-12 rounded-lg bg-brand-500 text-slate-950 flex items-center justify-center"><SunDim size={30} weight="bold" /></span>
              <div>
                <div className="font-heading text-2xl font-extrabold tracking-tight">ANUJ SOLAR</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-brand-400 font-bold">Material Dispatch Slip</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[9px] uppercase tracking-[0.2em] text-slate-400 font-bold">Dispatch ID</div>
              <div className="font-mono text-xl font-bold text-brand-400" data-testid="slip-dispatch-no">{slip.dispatch_no}</div>
              <div className="text-[11px] text-slate-300 font-mono mt-1">{dmy(slip.dispatch_date)} · {time12(slip.dispatch_time)}</div>
            </div>
          </div>
          <div className="h-1 bg-gradient-to-r from-brand-500 via-brand-400 to-orange-500" />

          <div className="px-8 py-6 grid grid-cols-[1fr_auto] gap-8 border-b border-slate-200">
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <Field label="Customer" value={slip.customer_name} testid="slip-customer" />
              <Field label="Mobile" value={slip.customer_mobile} mono />
              <div className="col-span-2"><Field label="Site Address" value={slip.customer_address} /></div>
              <Field label="Vehicle Number" value={slip.vehicle_no} mono testid="slip-vehicle" />
              <Field label="Driver Name" value={slip.driver_name} testid="slip-driver" />
              <Field label="Dispatched By" value="Warehouse" />
              <Field label="Total Quantity" value={`${fmt(totalQty)} units · ${(slip.items || []).length} items`} />
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <div className="p-2 rounded-lg ring-1 ring-slate-200 bg-white" data-testid="slip-qr">
                <QRCodeCanvas value={slipUrl(slip.token)} size={132} level="M" />
              </div>
              <div className="text-[9px] uppercase tracking-[0.14em] text-slate-400 font-bold">Scan to verify</div>
            </div>
          </div>

          <div className="px-8 py-6">
            <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">Material Details</div>
            <table className="w-full text-[12px] border border-slate-200" data-testid="slip-items">
              <thead>
                <tr className="bg-slate-100 text-slate-600 text-[10px] uppercase tracking-wider">
                  <th className="text-left px-3 py-2 w-8">#</th><th className="text-left px-3 py-2">Material</th>
                  <th className="text-right px-3 py-2 w-24">Qty</th><th className="text-left px-3 py-2 w-[40%]">Serial Numbers</th>
                </tr>
              </thead>
              <tbody>
                {(slip.items || []).map((it, i) => (
                  <tr key={i} className="border-t border-slate-200 align-top">
                    <td className="px-3 py-2.5 font-mono text-slate-400">{i + 1}</td>
                    <td className="px-3 py-2.5"><div className="flex items-center gap-2.5"><MaterialIcon iconKey={it.icon_key || it.label} size={28} /><span className="font-semibold">{it.label}</span></div></td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{fmt(it.qty)} {it.unit}</td>
                    <td className="px-3 py-2.5 font-mono text-[10.5px] leading-relaxed text-slate-700">{(it.serials || []).length ? it.serials.join(", ") : <span className="text-slate-300">—</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {slip.note && <div className="mt-4 text-xs text-slate-600"><b>Note:</b> {slip.note}</div>}
          </div>

          {(slip.photos || []).length > 0 && (
            <div className="px-8 pb-6">
              <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500 mb-2">Dispatch Photos ({slip.photos.length})</div>
              <div className="grid grid-cols-5 gap-2">
                {slip.photos.map((p) => (
                  <div key={p.id} className="aspect-square rounded-md overflow-hidden ring-1 ring-slate-200 bg-slate-100">
                    {photos[p.id] && <img src={photos[p.id]} alt="" className="w-full h-full object-cover" />}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="px-8 py-5 border-t border-slate-200 grid grid-cols-3 gap-6 text-[10px] text-slate-500">
            {["Warehouse Sign", "Driver Sign", "Receiver Sign"].map((s) => (
              <div key={s}><div className="h-10 border-b border-dashed border-slate-300" /><div className="mt-1 uppercase tracking-wider font-bold">{s}</div></div>
            ))}
          </div>
          <div className="px-8 py-3 bg-slate-50 text-[9px] text-slate-400 flex items-center justify-between">
            <span className="flex items-center gap-1"><SealCheck size={12} className="text-emerald-500" /> System generated slip · Anuj Solar Workflow ERP</span>
            <span className="font-mono">{slip.token}</span>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
